import { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { ReportItem } from "@/constants/ReportDashboard/report-items"
import { useTopBottomSelectedPortfolioFactors } from '@/contexts/FactorLensContext/hooks'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { SecurityContributionsData } from '@/interfaces/factorLens.interface'
import { cardPrefsContextKey, FactorContrib, RADashboardCardPrefs } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import {
  cardTitleStyle,
  simpleCharacterButtonStyle,
} from '@/styles/reportDashboardStyles'
import { getHeader } from '@/utils/factorAnalytics.util'
import { getGridThemeClass } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef, ColumnMovedEvent, ColumnResizedEvent, ColumnState, GridApi, GridReadyEvent } from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// toLocaleString() defaults to 3 fraction digits, so values like -0.0001
// render as "-0". Treat anything that rounds to zero at that precision as 0.
const numFormatter = new Intl.NumberFormat(navigator.language, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const formatNumberShort = (value: unknown) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '0.00'
  if (Math.abs(value) < 0.005) return '0.00'
  return numFormatter.format(value);
}

// Raw (unformatted) cell value — shared by export and copy.
const clipboardCellValue = (p: any) => {
  const field = p.column?.getColDef?.()?.field
  return field && p.node?.data && p.node.data[field] !== undefined ? p.node.data[field] : p.value
}

const FactorContributions: React.FC<SecurityContributionsData> = (props) => {
  const theme = useTheme()

  // Both sub-tables (Top / Bottom) expose their grid API here so a single
  // card-level export can combine them.
  const topApiRef = useRef<GridApi | null>(null)
  const bottomApiRef = useRef<GridApi | null>(null)
  const setTopApi = useCallback((api: GridApi) => { topApiRef.current = api }, [])
  const setBottomApi = useCallback((api: GridApi) => { bottomApiRef.current = api }, [])
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null,
  )

  const topBottomSecurities = useTopBottomSelectedPortfolioFactors();

  const downloadCsv = (fileName: string, content: string) => {
    const blob = new Blob(['﻿' + content], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    const tApi = topApiRef.current
    const bApi = bottomApiRef.current
    // Export raw, full-precision values — bypass the 2-decimal display formatter.
    const processCellCallback = clipboardCellValue
    if (type === 'EXCEL') {
      // One workbook, two sheets (Top / Bottom).
      const sheets = [
        tApi?.getSheetDataForExcel({ sheetName: 'Top Contributors', processCellCallback }),
        bApi?.getSheetDataForExcel({ sheetName: 'Bottom Contributors', processCellCallback }),
      ].filter((s): s is string => !!s)
      const api = tApi ?? bApi
      if (api && sheets.length)
        api.exportMultipleSheetsAsExcel({
          data: sheets,
          fileName: 'security_contribution',
        })
    } else {
      // One CSV file, two labelled sections.
      const parts: string[] = []
      if (tApi)
        parts.push('Top 5 Contributors\n' + tApi.getDataAsCsv({ processCellCallback }))
      if (bApi)
        parts.push('Bottom 5 Contributors\n' + bApi.getDataAsCsv({ processCellCallback }))
      if (parts.length)
        downloadCsv('security_contribution.csv', parts.join('\n\n'))
    }
    setExportMenuAnchor(null)
  }

  const tableTitleStyle = {
    color: theme.textPrimary,
    fontSize: '0.875rem' as const,
    fontWeight: 600,
    marginBottom: 6,
    paddingBottom: 4,
    borderBottom: `1px solid ${theme.border}`,
  }

  return (
    <>
      {props.shown ? (
        <div
          style={{
            flex: 4,
            borderRadius: '8px',
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.cardBackground,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            marginTop: 8,
            marginBottom: 8
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              style={{
                ...cardTitleStyle,
                marginLeft: 8,
                color: theme.textPrimary,
              }}
            >
              Security Contribution
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Export menu (3-dot) — Top + Bottom combined (Excel: 2 sheets, CSV: 2 sections) */}
              <IconButton
                size='small'
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ color: theme.textSecondary, padding: '4px' }}
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    minWidth: 140,
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleExport('EXCEL')}
                  sx={{ fontSize: '14px', color: theme.textPrimary, '&:hover': { backgroundColor: theme.border } }}
                >
                  Export as Excel
                </MenuItem>
                <MenuItem
                  onClick={() => handleExport('CSV')}
                  sx={{ fontSize: '14px', color: theme.textPrimary, '&:hover': { backgroundColor: theme.border } }}
                >
                  Export as CSV
                </MenuItem>
              </Menu>
              <Button
                style={{
                  ...simpleCharacterButtonStyle,
                  color: theme.textPrimary,
                }}
                onClick={() => props.toggleVisibility()}
              >
                {'❭'}
              </Button>
            </Box>
          </div>

          <div
            style={{
              flex: 1,
              margin: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              sx={{
                mb: '6px',
                pb: '4px',
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 14, color: COMPANY_COLORS.SUCCESS }} />
              <Typography
                style={{
                  ...tableTitleStyle,
                  borderBottom: 'none',
                  marginBottom: 0,
                  paddingBottom: 0,
                  color: COMPANY_COLORS.SUCCESS,
                }}
              >
                Top 5 Contributors
              </Typography>
            </Stack>
              <SecuritiesTable
                data={topBottomSecurities.topItems}
                isTop={true}
                cardPrefs={props.cardPrefs}
                onCardPrefsChange={props.onCardPrefsChange}
                onApiReady={setTopApi}
              />
          </div>

          <div
            style={{
              flex: 1,
              margin: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              sx={{
                mb: '6px',
                pb: '4px',
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <TrendingDownIcon sx={{ fontSize: 14, color: COMPANY_COLORS.ERROR }} />
              <Typography
                style={{
                  ...tableTitleStyle,
                  borderBottom: 'none',
                  marginBottom: 0,
                  paddingBottom: 0,
                  color: COMPANY_COLORS.ERROR,
                }}
              >
                Bottom 5 Contributors
              </Typography>
            </Stack>
              <SecuritiesTable
                data={topBottomSecurities.bottomItems}
                isTop={false}
                cardPrefs={props.cardPrefs}
                onCardPrefsChange={props.onCardPrefsChange}
                onApiReady={setBottomApi}
              />
          </div>
        </div>
      ) : (
        <div
          style={{
            width: '4rem',
            borderRadius: '8px',
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.cardBackground,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: 8,
            marginBottom: 8
          }}
        >
          <Button
            style={{ ...simpleCharacterButtonStyle, color: theme.textPrimary }}
            onClick={() => props.toggleVisibility()}
          >
            {'❬'}
          </Button>
          <Typography
            style={{
              color: theme.textSecondary,
              fontSize: '1rem',
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              margin: 'auto',
            }}
          >
            Security Contribution
          </Typography>
        </div>
      )}
    </>
  )
}

interface SecuritiesTableProps {
  data: FactorContrib[]
  isTop: boolean
  cardPrefs?: RADashboardCardPrefs
  onCardPrefsChange?: (newPrefs: RADashboardCardPrefs) => void
  onApiReady?: (api: GridApi) => void
}

const SecuritiesTable = ({ data, isTop, cardPrefs, onCardPrefsChange, onApiReady }: SecuritiesTableProps) => {
  const securities = data ?? []
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const accentColor = isTop ? COMPANY_COLORS.SUCCESS : COMPANY_COLORS.ERROR

  const gridRef = useRef<AgGridReact>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => gridRef.current?.api)
  const gridContextMenuItems = useMemo(() => buildCopyContextMenuItems({ processCell: clipboardCellValue }), [])
  const cardPrefsRef = useRef(cardPrefs)
  const onCardPrefsChangeRef = useRef(onCardPrefsChange)
  useEffect(() => { cardPrefsRef.current = cardPrefs }, [cardPrefs])
  useEffect(() => { onCardPrefsChangeRef.current = onCardPrefsChange }, [onCardPrefsChange])

  const initializedForDashboard = useRef<string | null>(null)
  const applyingRef = useRef(false)

  const stateKey: 'topContributorsColumnState' | 'bottomContributorsColumnState' =
    isTop ? 'topContributorsColumnState' : 'bottomContributorsColumnState'

  const applyColState = useCallback((saved?: Record<string, unknown>[]) => {
    const api = gridRef.current?.api
    if (!api || !saved) return
    applyingRef.current = true
    api.applyColumnState({ state: saved as unknown as ColumnState[], applyOrder: true })
    setTimeout(() => { applyingRef.current = false }, 0)
  }, [])

  const saveGridState = useCallback(() => {
    if (applyingRef.current) return
    const api = gridRef.current?.api
    const prefs = cardPrefsRef.current
    if (!api || !prefs?.dashboardName) return
    const updated: RADashboardCardPrefs = {
      ...prefs,
      factorLens: {
        ...prefs.factorLens,
        [stateKey]: api.getColumnState() as unknown as Record<string, unknown>[],
      },
    }
    onCardPrefsChangeRef.current?.(updated)
    putUserPreference(cardPrefsContextKey(prefs.dashboardName), updated)
  }, [stateKey])

  const onGridReady = useCallback((e: GridReadyEvent) => {
    onApiReady?.(e.api)
    const prefs = cardPrefsRef.current
    const saved = prefs?.factorLens?.[stateKey]
    if (saved) {
      initializedForDashboard.current = prefs?.dashboardName ?? null
      applyColState(saved)
    }
    gridRef.current?.api?.sizeColumnsToFit()
  }, [applyColState, stateKey, onApiReady])

  useEffect(() => {
    const dashboardName = cardPrefs?.dashboardName
    const saved = cardPrefs?.factorLens?.[stateKey]
    if (saved && gridRef.current?.api && dashboardName !== initializedForDashboard.current) {
      initializedForDashboard.current = dashboardName ?? null
      applyColState(saved)
    }
  }, [cardPrefs, applyColState, stateKey])

  const onColumnMoved = useCallback((e: ColumnMovedEvent) => {
    if (e.finished) saveGridState()
  }, [saveGridState])

  const onColumnResized = useCallback((e: ColumnResizedEvent) => {
    if (e.finished) saveGridState()
  }, [saveGridState])

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: getHeader(ReportItem.StandardID),
        field: 'security_id',
        flex: 1,
        filter: false,
        cellStyle: () => ({ color: theme.textPrimary, cursor: 'pointer' }),
      },
      {
        headerName: getHeader(ReportItem.Description),
        field: 'description',
        flex: 2,
        filter: false,
        cellStyle: () => ({ color: theme.textPrimary, cursor: 'pointer' }),
      },
      {
        headerName: 'Risk',
        field: 'security_risk',
        flex: 1,
        filter: false,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => formatNumberShort(params.value),
        cellStyle: () => ({ color: theme.textPrimary }),
      },
      {
        headerName: 'Weight (%)',
        field: 'weighted_exposure',
        flex: 1,
        filter: false,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => formatNumberShort(params.value),
        cellStyle: () => ({ color: theme.textPrimary }),
      },
      {
        headerName: 'Contribution (bps)',
        field: 'risk_contrib',
        flex: 1,
        filter: false,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => formatNumberShort(params.value),
        cellStyle: () => ({ color: accentColor, fontWeight: 600 }),
      },
    ],
    [theme, accentColor],
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      suppressMenu: true,
      floatingFilter: false,
      suppressSizeToFit: false,
    }),
    [],
  )

  const gridOptions = useMemo(
    () => ({
      suppressRowClickSelection: true,
      animateRows: true,
      pagination: false,
      suppressPaginationPanel: true,
      suppressRowHoverHighlight: false,
      suppressMenuHide: true,
      enableBrowserTooltips: true,
      tooltipShowDelay: 500,
      suppressNoRowsOverlay: false,
      domLayout: 'autoHeight' as const,
    }),
    [],
  )

  const rowData = useMemo(() => securities.map(s => ({ ...s })), [securities])

  return (
    <Box
      ref={gridBoxRef}
      sx={[
        {
          width: '100%',
          border: `1px solid ${theme.border}`,
          borderRadius: 1,
          overflow: 'hidden',
          // cwan theme drives colors; keep grid-specific tokens only.
          '--ag-secondary-foreground-color': theme.textSecondary,
          '--ag-row-hover-color': getColorWithOpacity(theme.border, 0.4),
          '--ag-input-focus-border-color': theme.border,
          '--ag-checkbox-unchecked-color': theme.textSecondary,
          '--ag-checkbox-checked-color': theme.textPrimary,
          '--ag-input-border-color': theme.border,
          '--ag-modal-overlay-background-color': getColorWithOpacity(theme.cardBackground, 0.66),
          '--ag-control-panel-background-color': theme.cardBackground,
          '--ag-disabled-foreground-color': getColorWithOpacity(theme.textSecondary, 0.5),
          '--ag-chip-background-color': theme.buttonBackground,
        },
        gridHeaderIconsSx,
      ]}
      className={getGridThemeClass(isDarkMode)}
    >
      <AgGridReact
        ref={gridRef}
        containerStyle={{ width: '100%' }}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        gridOptions={gridOptions}
        modules={[ClientSideRowModelModule, CsvExportModule, ExcelExportModule, ClipboardModule, RangeSelectionModule, MenuModule]}
        columnHoverHighlight
        enableRangeSelection
        popupParent={document.body}
        getContextMenuItems={gridContextMenuItems}
        processCellForClipboard={clipboardCellValue}
        onGridReady={onGridReady}
        onColumnMoved={onColumnMoved}
        onColumnResized={onColumnResized}
        onSortChanged={saveGridState}
        onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
        noRowsOverlayComponent={() => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Typography sx={{ color: theme.textSecondary }}>
              No data found
            </Typography>
          </Box>
        )}
      />
    </Box>
  )
}

export default FactorContributions
